<template>
  <MAFieldWrapper
    :label="label"
    :value="formattedValue"
    :disabled="disabled"
    :required="required"
    :readonly="readonly"
    :placeholder="placeholder"
    :rules="rules"
  >
    <template #default="{ validator, handleBlur }">
      <div class="relative">
        <div
          ref="prefixWrapper"
          class="absolute inset-y-0 ma-text-secondary h-full left-0 flex items-center rounded-md select-none pointer-events-none"
          :class="placeholder && !value ? 'opacity-0 w-2' : 'opacity-90 px-2'"
        >
          {{ prefix }}
        </div>
        <InputText
          v-model="value"
          :type="type"
          :invalid="validator?.$dirty && validator?.$invalid"
          :disabled="disabled"
          :autofocus="autofocus"
          :placeholder="placeholder"
          class="w-full grow"
          :style="{ paddingLeft: `${width + 8}px` }"
          :size="size ?? undefined"
          @blur="onBlur(handleBlur)"
        />
      </div>
    </template>
  </MAFieldWrapper>
</template>

<script setup lang="ts">
import InputText from "@/volt/InputText.vue";
import { ulid } from "ulid";
import MAFieldWrapper from "./MAFieldWrapper.vue";

const props = withDefaults(
  defineProps<{
    type?: "email" | "text" | "phone" | "url" | "password";
    label?: string;
    modelValue?: string;
    disabled?: boolean;
    required?: boolean;
    readonly?: boolean;
    placeholder?: string;
    autofocus?: boolean;
    size?: "large" | "small" | null;
    rules?: Record<string, any>;
    prefix?: string;
  }>(),
  {
    id: ulid(),
    disabled: false,
    placeholder: "",
    modelValue: "",
    type: "text",
    rules: () => ({}),
  }
);

const prefixWrapper = ref<HTMLDivElement>();
const { width } = useElementSize(prefixWrapper);
const emit = defineEmits(["update:modelValue", "blur"]);

// Internal state without "https://"
const value = computed({
  get: () =>
    !!props.prefix && props.modelValue?.startsWith(props.prefix || "")
      ? props.modelValue.slice(props.prefix?.length)
      : props.modelValue,
  set: (val: string) =>
    emit(
      "update:modelValue",
      val.startsWith(props.prefix || "") ? val : `${props.prefix || ""}${val}`
    ),
});
const formattedValue = computed(() => `${props.prefix || ""}${value.value}`);

function onBlur(cb?: () => void) {
  if (cb) cb();
  emit("blur");
}

onMounted(() => {
  if (props.autofocus) {
    const input = prefixWrapper.value?.nextElementSibling as HTMLInputElement;
    if (input) input.focus();
  }
});
</script>
